import React, { useLayoutEffect, useRef, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';
import { Field, Form } from 'react-final-form';
import moment from 'moment';

import CircularProgress from '@material-ui/core/CircularProgress';
import Download from '@material-ui/icons/GetApp';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import makeStyles from '@material-ui/core/styles/makeStyles';

import ActionButton from 'components/buttons/ActionButton';
import { Dialog } from 'components/common';
import { Input } from 'components/form';
import FilterDateInput from 'components/inputs/FilterDateInput';
import { number, composeValidators, maxValue } from 'components/form/validationRules';
import { NumberFormat, numberInputFormatter } from 'services/helpers/inputFormatters';

const initialValues = { minSpend: '', maxRecords: '', date: { from: null, to: null } };

const useStyles = makeStyles((theme) => ({
  button: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
}));

const decodeDateParam = (dateParam) => decodeURIComponent(dateParam);

const ExportTopSpendersButton = (props) => {
  const classes = useStyles();

  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const mountedRef = useRef(false);
  const runningRef = useRef(false);
  const operationRef = useRef(0);
  const timeoutRef = useRef<number | null>(null);

  useLayoutEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      runningRef.current = false;
      operationRef.current += 1;
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
  }, []);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleExportDebtors = async ({ minSpend, maxRecords, date }) => {
    if (!mountedRef.current || runningRef.current) return;
    runningRef.current = true;
    const operation = ++operationRef.current;
    const isCurrent = () => mountedRef.current && operationRef.current === operation;
    const finish = () => {
      if (!isCurrent()) return;
      runningRef.current = false;
      setLoading(false);
    };
    setLoading(true);

    const poll = async (executionArn: string) => {
      if (!isCurrent()) return;
      timeoutRef.current = null;
      try {
        const {
          data: { status, downloadURL },
        } = await dataProvider.get('exportReportStatus', {
          arn: executionArn,
        });
        if (!isCurrent()) return;
        if (status === 'RUNNING') {
          timeoutRef.current = window.setTimeout(() => poll(executionArn), 5000);
          return;
        }
        if (status === 'SUCCEEDED' && downloadURL) {
          const response = await fetch(downloadURL);
          if (!isCurrent()) return;
          if (!response.ok) throw new Error(`Failed to download report (${response.status}).`);
          const text = await response.text();
          if (!isCurrent()) return;
          const url = window.URL.createObjectURL(new Blob([text]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Top_Spenders_Report_${moment().format()}.csv`);
          document.body.appendChild(link);
          try {
            link.click();
            handleCloseDialog();
          } finally {
            link.remove();
            window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
          }
        }
        finish();
      } catch (error) {
        if (!isCurrent()) return;
        finish();
        notify(error?.message || 'Failed to export top spenders');
      }
    };

    try {
      const params: any = {};
      if (minSpend) params.minSpend = minSpend;
      if (maxRecords) params.maxRecords = maxRecords;
      if (date?.from) params.startDate = decodeDateParam(date.from);
      if (date?.to) params.endDate = decodeDateParam(date.to);

      const {
        data: { executionArn },
      } = await dataProvider.get('exportTopSpenders', params);

      if (!isCurrent()) return;
      timeoutRef.current = window.setTimeout(() => poll(executionArn), 5000);
    } catch (error) {
      if (!isCurrent()) return;
      finish();
      notify(error?.message || 'Failed to export top spenders');
    }
  };

  return (
    <>
      <ActionButton
        disabled={loading}
        onClick={handleOpenDialog}
        classes={{
          label: classes.button,
        }}>
        Export Top Spenders {loading ? <CircularProgress size={20} /> : <Download />}
      </ActionButton>
      {openDialog && (
        <Form
          onSubmit={handleExportDebtors}
          initialValues={initialValues}
          render={({ handleSubmit, submitting }) => {
            return (
              <form onSubmit={handleSubmit}>
                <Dialog
                  open={openDialog}
                  onCancel={handleCloseDialog}
                  title="Export Top Spenders"
                  onConfirm={handleSubmit}
                  confirmText="Export"
                  submitting={submitting || loading}>
                  <Input
                    name="minSpend"
                    label="Minimum Spend"
                    validate={composeValidators(number, maxValue(1000000))}
                    formatValue={numberInputFormatter[NumberFormat.Integer]}
                  />
                  <Input
                    name="maxRecords"
                    label="Maximum Records"
                    validate={composeValidators(number, maxValue(1000000))}
                    formatValue={numberInputFormatter[NumberFormat.Integer]}
                  />
                  <Field
                    name="date"
                    label="Date"
                    render={(inputProps) => (
                      <FilterDateInput
                        {...inputProps}
                        icon={AccessTimeIcon}
                        label="Date"
                        allowEmpty
                      />
                    )}
                  />
                </Dialog>
              </form>
            );
          }}
        />
      )}
    </>
  );
};

export default ExportTopSpendersButton;
